import {
  classifyInsuredOpportunities,
  EmptyStateWithActions,
  useChat,
  useInsuredOpportunityEmptyStateProps,
} from "@qw/chat";
import { type MyInvokablePlaybooksQuery, useMyInvokablePlaybooksQuery } from "@qw/graphql";
import { Icon } from "@qw/ui";
import { type ComponentProps, useCallback, useMemo } from "react";
import { useParams } from "react-router";
import { useInboxQueryStates } from "@/providers/inbox-query-states-provider";
import { useConversation } from "@/providers/use-conversation";
import { useInboxInsured } from "@/providers/use-inbox-insured";

export const ChatEmptyState = () => {
  const { conversationId } = useInboxQueryStates();
  const { insuredId } = useParams();
  const { inboxConversation } = useConversation();
  const { opportunities } = useInboxInsured();
  const { isReadOnly, sendMessage } = useChat();

  // Empty-state suggestions kick off playbooks from foreground chat, so only
  // type=Workflow is eligible. myInvokablePlaybooks returns owned + subscribed
  // already filtered server-side.
  const { data: invokablePlaybooksData } = useMyInvokablePlaybooksQuery();
  const allUserPlaybooks = useMemo(() => invokablePlaybooksData?.myInvokablePlaybooks ?? [], [invokablePlaybooksData]);

  const findPlaybooks = useCallback(
    (playbookName: string) =>
      allUserPlaybooks.filter((playbook) => playbook.name.toLowerCase().includes(playbookName.toLowerCase())),
    [allUserPlaybooks]
  );
  const { renewals } = useMemo(() => classifyInsuredOpportunities({ opportunities }), [opportunities]);
  const insuredOpportunityEmptyStateProps = useInsuredOpportunityEmptyStateProps({ opportunities, sendMessage });

  const getPlaybookAction = useCallback(
    (
      icon: ComponentProps<typeof Icon>["icon"],
      playbook: MyInvokablePlaybooksQuery["myInvokablePlaybooks"][number]
    ) => ({
      icon: <Icon icon={icon} />,
      label: `Run ${playbook.name}${playbook.name.toLowerCase().includes("playbook") ? "" : " playbook"}`,
      onClick: () => sendMessage({ text: `Run playbook "${playbook.name}" (ID: ${playbook.id})` }),
    }),
    [sendMessage]
  );

  const emptyStateProps = useMemo(() => {
    if (isReadOnly) {
      return {
        className: "items-center justify-center p-8",
        description:
          "This conversation belongs to another user. You can view the chat history but cannot send messages.",
      };
    }

    if (insuredId && !conversationId && insuredOpportunityEmptyStateProps) {
      return {
        ...insuredOpportunityEmptyStateProps,
        actions:
          renewals.length > 0
            ? [
                ...findPlaybooks("renewal").map((playbook) => getPlaybookAction("autorenew", playbook)),
                ...(insuredOpportunityEmptyStateProps.actions ?? []),
              ]
            : insuredOpportunityEmptyStateProps.actions,
        className: "justify-end pb-3.5",
      };
    }

    if (insuredId && !conversationId) {
      return {
        actions: [
          ...findPlaybooks("intake").map((playbook) => getPlaybookAction("add_task", playbook)),
          ...findPlaybooks("submission setup").map((playbook) => getPlaybookAction("add_task", playbook)),
        ],
        className: "justify-end pb-3.5",
        description: "Let me help you with this insured",
      };
    }

    if (conversationId && inboxConversation?.insuredId) {
      return {
        actions: [
          ...findPlaybooks("quote processing").map((playbook) => getPlaybookAction("summarize", playbook)),
          ...findPlaybooks("blocked").map((playbook) => getPlaybookAction("back_hand", playbook)),
          ...findPlaybooks("declination").map((playbook) => getPlaybookAction("dangerous", playbook)),
        ],
        className: "justify-end pb-3.5",
        description: (
          <>
            This conversation is linked to {inboxConversation.insured?.name || "an insured"}. Ask me anything about this
            conversation or the insured.
          </>
        ),
      };
    }

    if (conversationId && !inboxConversation?.insuredId) {
      return {
        actions: [
          {
            icon: <Icon icon="link" />,
            label: "Link to insured using subject line",
            onClick: () => sendMessage({ text: "link to insured using the conversation subject line" }),
          },
          ...findPlaybooks("intake").map((playbook) => getPlaybookAction("add_task", playbook)),
          ...findPlaybooks("submission setup").map((playbook) => getPlaybookAction("add_task", playbook)),
        ],
        className: "justify-end pb-3.5",
        description: "Let me help you link this conversation to an insured or run the intake playbook.",
      };
    }

    return {
      className: "items-center justify-center p-8",
      description: "I'm here to help with your insurance documents, insureds, and email conversations.",
    };
  }, [
    isReadOnly,
    conversationId,
    findPlaybooks,
    getPlaybookAction,
    inboxConversation,
    insuredId,
    opportunities,
    insuredOpportunityEmptyStateProps,
    renewals.length,
  ]);

  return <EmptyStateWithActions {...emptyStateProps} />;
};
